import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { authService, tareasService, cursosService } from '../services/api';
import HtmlContent from '../components/HtmlContent';
import Pagination from '../components/Pagination';
import ConfirmPopup from '../components/ConfirmPopup';
import { formatPersonName } from '../utils/personName';
import { TIPOS_ARCHIVO_ENTREGA } from '../constants/tareaArchivoTipos';
import './Tareas.css';

const Tareas = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [tareas, setTareas] = useState([]);
  const [cursos, setCursos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCursoId, setSelectedCursoId] = useState('');
  const [mostrarCursosInactivos, setMostrarCursosInactivos] = useState(false);
  const [error, setError] = useState('');
  const [confirmEliminar, setConfirmEliminar] = useState({ show: false, tarea: null });
  const [currentUser, setCurrentUser] = useState(null);
  const [selectedRole, setSelectedRole] = useState(null);
  const [isProfesor, setIsProfesor] = useState(false);
  const [isCoordinador, setIsCoordinador] = useState(false);
  const [isAyudante, setIsAyudante] = useState(false);
  const [tabActiva, setTabActiva] = useState('listado'); // 'listado' | 'correccion' | 'habilitar'
  const [tabCorreccion, setTabCorreccion] = useState('pendientes'); // 'pendientes' | 'corregidas'
  const [habCursoId, setHabCursoId] = useState('');
  const [habTareaId, setHabTareaId] = useState('');
  const [planilla, setPlanilla] = useState(null);
  const [loadingPlanilla, setLoadingPlanilla] = useState(false);
  const [errorPlanilla, setErrorPlanilla] = useState('');
  const [habilitarForm, setHabilitarForm] = useState({ show: false, estudiante: null, fechaLimite: '' });
  const [savingHab, setSavingHab] = useState(false);
  const [confirmDeshab, setConfirmDeshab] = useState({ show: false, estudiante: null });
  const [grupoDetalle, setGrupoDetalle] = useState({ show: false, nombre: '', alumnos: [] });
  const [previewTarea, setPreviewTarea] = useState(null);
  const [pendientes, setPendientes] = useState([]);
  const [totalPendientes, setTotalPendientes] = useState(0);
  const [loadingPendientes, setLoadingPendientes] = useState(false);
  const [corregidas, setCorregidas] = useState([]);
  const [totalCorregidas, setTotalCorregidas] = useState(0);
  const [loadingCorregidas, setLoadingCorregidas] = useState(false);
  const [cursoFiltroCorreccion, setCursoFiltroCorreccion] = useState(''); // '' = todos, o curso_id
  const [busquedaCorreccion, setBusquedaCorreccion] = useState('');
  const [busquedaHab, setBusquedaHab] = useState('');
  const [pageListado, setPageListado] = useState(1);
  const [pagePendientes, setPagePendientes] = useState(1);
  const [pageCorregidas, setPageCorregidas] = useState(1);
  const [pageHab, setPageHab] = useState(1);
  const itemsPerPage = 10;
  const pendientesReqRef = useRef(0);
  const corregidasReqRef = useRef(0);
  const pagePendientesRef = useRef(pagePendientes);
  const pageCorregidasRef = useRef(pageCorregidas);
  const cursoFiltroCorreccionRef = useRef(cursoFiltroCorreccion);
  const busquedaCorreccionRef = useRef('');
  pagePendientesRef.current = pagePendientes;
  pageCorregidasRef.current = pageCorregidas;
  cursoFiltroCorreccionRef.current = cursoFiltroCorreccion;

  useEffect(() => {
    // Verificar autenticación
    if (!authService.isAuthenticated()) {
      navigate('/login');
      return;
    }

    const user = authService.getCurrentUser();
    setCurrentUser(user);

    const savedRole = localStorage.getItem('selectedRole');
    if (savedRole) {
      setSelectedRole(savedRole);
      setIsProfesor(savedRole === 'Profesor');
      setIsCoordinador(savedRole === 'Coordinador');
      setIsAyudante(savedRole === 'Ayudante');
    } else if (user && user.groups) {
      const grupos = Array.isArray(user.groups) ? user.groups : [user.groups];
      const primerRol = typeof grupos[0] === 'string' ? grupos[0] : (grupos[0].name || String(grupos[0]));
      setSelectedRole(primerRol);
      setIsProfesor(primerRol === 'Profesor');
      setIsCoordinador(primerRol === 'Coordinador');
      setIsAyudante(primerRol === 'Ayudante');
      localStorage.setItem('selectedRole', primerRol);
    }

    loadTareas();
    loadCursos();
  }, [navigate]);

  useEffect(() => {
    if (currentUser !== null) {
      loadCursos();
    }
  }, [currentUser, isProfesor, isCoordinador]);

  const cursosParaSelector = mostrarCursosInactivos
    ? (cursos || []).filter(c => c.activo === false)
    : (cursos || []).filter(c => c.activo !== false);

  useEffect(() => {
    const list = mostrarCursosInactivos ? (cursos || []).filter(c => c.activo === false) : (cursos || []).filter(c => c.activo !== false);
    // Solo vaciar si hay lista cargada y el curso seleccionado no está en ella (no vaciar mientras cursos sigue cargando)
    if (selectedCursoId && list.length > 0 && !list.some(c => String(c.id) === String(selectedCursoId))) {
      setSelectedCursoId('');
    }
  }, [mostrarCursosInactivos, cursos, selectedCursoId]);

  useEffect(() => {
    const handleRoleChange = (event) => {
      const newRole = event.detail?.role || localStorage.getItem('selectedRole');
      if (newRole && newRole !== selectedRole) {
        setSelectedRole(newRole);
        setIsProfesor(newRole === 'Profesor');
        setIsCoordinador(newRole === 'Coordinador');
        setIsAyudante(newRole === 'Ayudante');
      }
    };
    window.addEventListener('roleChanged', handleRoleChange);
    window.addEventListener('storage', handleRoleChange);
    return () => {
      window.removeEventListener('roleChanged', handleRoleChange);
      window.removeEventListener('storage', handleRoleChange);
    };
  }, [selectedRole]);

  // Ayudante solo ve la pestaña de corrección; forzar tab y no mostrar listado
  useEffect(() => {
    if (selectedRole === 'Ayudante') {
      setTabActiva('correccion');
    }
  }, [selectedRole]);

  const [avisoYaCorregida, setAvisoYaCorregida] = useState('');
  const [refreshPendientesTick, setRefreshPendientesTick] = useState(0);

  // Al volver desde corrección o ver corrección: abrir pestaña y restaurar filtro/página
  useEffect(() => {
    const state = location.state;
    if (state?.tab === 'correccion' && (state?.subTab === 'pendientes' || state?.subTab === 'corregidas')) {
      setTabActiva('correccion');
      setTabCorreccion(state.subTab);
      if (state.cursoFiltro !== undefined && state.cursoFiltro !== null) {
        setCursoFiltroCorreccion(String(state.cursoFiltro));
      }
      if (typeof state.page === 'number' && state.page >= 1) {
        if (state.subTab === 'pendientes') setPagePendientes(state.page);
        else setPageCorregidas(state.page);
      }
      if (state.refreshPendientes) {
        setRefreshPendientesTick((n) => n + 1);
      }
    }
  }, [location.state]);

  // Al volver desde Editar Tarea (cancelar): listado de tareas con el curso seleccionado
  useEffect(() => {
    const state = location.state;
    if (state?.selectedCursoId) {
      setTabActiva('listado');
      setSelectedCursoId(String(state.selectedCursoId));
    }
  }, [location.state]);

  const loadPendientes = useCallback(async (pageOverride) => {
    const page = pageOverride != null ? pageOverride : pagePendientesRef.current;
    const reqId = ++pendientesReqRef.current;
    try {
      setLoadingPendientes(true);
      const params = { page, page_size: itemsPerPage };
      const filtro = cursoFiltroCorreccionRef.current;
      if (filtro) params.curso_id = filtro;
      const q = (busquedaCorreccionRef.current || '').trim();
      if (q) params.q = q;
      const { results, total } = await tareasService.getEntregasPendientes(params);
      if (reqId !== pendientesReqRef.current) return;
      setPendientes(Array.isArray(results) ? results : []);
      setTotalPendientes(typeof total === 'number' ? total : 0);
    } catch (err) {
      if (reqId !== pendientesReqRef.current) return;
      setError('Error al cargar entregas pendientes');
      setPendientes([]);
      setTotalPendientes(0);
      console.error(err);
    } finally {
      if (reqId === pendientesReqRef.current) setLoadingPendientes(false);
    }
  }, [itemsPerPage]);

  useEffect(() => {
    if (refreshPendientesTick > 0) {
      loadPendientes(pagePendientesRef.current);
    }
  }, [refreshPendientesTick, loadPendientes]);

  const loadCorregidas = useCallback(async (pageOverride) => {
    const page = pageOverride != null ? pageOverride : pageCorregidasRef.current;
    const reqId = ++corregidasReqRef.current;
    try {
      setLoadingCorregidas(true);
      const params = { page, page_size: itemsPerPage };
      const filtro = cursoFiltroCorreccionRef.current;
      if (filtro) params.curso_id = filtro;
      const q = (busquedaCorreccionRef.current || '').trim();
      if (q) params.q = q;
      const { results, total } = await tareasService.getEntregasCorregidas(params);
      if (reqId !== corregidasReqRef.current) return;
      setCorregidas(Array.isArray(results) ? results : []);
      setTotalCorregidas(typeof total === 'number' ? total : 0);
    } catch (err) {
      if (reqId !== corregidasReqRef.current) return;
      setError('Error al cargar entregas corregidas');
      setCorregidas([]);
      setTotalCorregidas(0);
      console.error(err);
    } finally {
      if (reqId === corregidasReqRef.current) setLoadingCorregidas(false);
    }
  }, [itemsPerPage]);

  const cambiarPaginaPendientes = useCallback((page) => {
    const p = Math.max(1, Number(page) || 1);
    if (p === pagePendientesRef.current) {
      loadPendientes(p);
    } else {
      setPagePendientes(p);
    }
  }, [loadPendientes]);

  const cambiarPaginaCorregidas = useCallback((page) => {
    const p = Math.max(1, Number(page) || 1);
    if (p === pageCorregidasRef.current) {
      loadCorregidas(p);
    } else {
      setPageCorregidas(p);
    }
  }, [loadCorregidas]);

  useEffect(() => {
    const t = setTimeout(() => {
      const q = busquedaCorreccion.trim();
      if (busquedaCorreccionRef.current === q) return;
      busquedaCorreccionRef.current = q;
      setPagePendientes(1);
      setPageCorregidas(1);
      if (!(tabActiva === 'correccion' || isAyudante)) return;
      if (tabCorreccion === 'pendientes') loadPendientes(1);
      else loadCorregidas(1);
    }, 300);
    return () => clearTimeout(t);
  }, [busquedaCorreccion, tabActiva, tabCorreccion, isAyudante, loadPendientes, loadCorregidas]);

  useEffect(() => {
    if (tabActiva === 'correccion' || isAyudante) {
      if (tabCorreccion === 'pendientes') loadPendientes(pagePendientes);
      else loadCorregidas(pageCorregidas);
    }
  }, [tabActiva, tabCorreccion, isAyudante, pagePendientes, pageCorregidas, cursoFiltroCorreccion, loadPendientes, loadCorregidas]);

  // Refrescar listas al volver a la pestaña (p. ej. tras una reentrega del estudiante)
  useEffect(() => {
    const onVisibility = () => {
      if (document.visibilityState !== 'visible') return;
      if (!(tabActiva === 'correccion' || isAyudante)) return;
      if (tabCorreccion === 'pendientes') loadPendientes();
      else loadCorregidas();
    };
    document.addEventListener('visibilitychange', onVisibility);
    return () => document.removeEventListener('visibilitychange', onVisibility);
  }, [tabActiva, tabCorreccion, isAyudante, loadPendientes, loadCorregidas]);

  // Cursos activos del equipo del usuario para el filtro de corrección
  const cursosUnicosCorreccion = useMemo(() => {
    return (cursos || [])
      .filter((c) => c.activo !== false)
      .map((c) => ({ id: c.id, nombre: c.nombre || 'Sin nombre' }))
      .sort((a, b) => (a.nombre || '').localeCompare(b.nombre || ''));
  }, [cursos]);

  const totalPagesPendientes = Math.max(1, Math.ceil(totalPendientes / itemsPerPage) || 1);
  const pagePendientesActual = Math.min(Math.max(1, pagePendientes), totalPagesPendientes);

  const totalPagesCorregidas = Math.max(1, Math.ceil(totalCorregidas / itemsPerPage) || 1);
  const pageCorregidasActual = Math.min(Math.max(1, pageCorregidas), totalPagesCorregidas);

  const loadCursos = async () => {
    try {
      const data = await cursosService.getCursos();
      // Solo mostrar cursos a los que el usuario está vinculado (en el equipo)
      if (isProfesor && !isCoordinador && currentUser) {
        const cursosFiltrados = (data || []).filter(curso => {
          if (curso.equipo && Array.isArray(curso.equipo)) {
            return curso.equipo.some(miembroId => {
              const id = typeof miembroId === 'object' && miembroId.id ? miembroId.id : miembroId;
              return parseInt(id) === parseInt(currentUser.id);
            });
          }
          return false;
        });
        setCursos(cursosFiltrados);
      } else {
        setCursos(data || []);
      }
    } catch (err) {
      const msg = err.response?.data?.error || 'Error al cargar cursos';
      setError(msg);
      setCursos([]);
      console.error('Error al cargar cursos:', err);
    }
  };

  const loadTareas = useCallback(async () => {
    try {
      setLoading(true);
      const data = await tareasService.getTareas();
      setTareas(data);
    } catch (err) {
      setError('Error al cargar tareas');
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, []);

  const cambiarPaginaListado = useCallback((page) => {
    const p = Math.max(1, Number(page) || 1);
    if (p === pageListado) {
      loadTareas();
    } else {
      setPageListado(p);
    }
  }, [pageListado, loadTareas]);

  const handleDelete = (tarea) => {
    setConfirmEliminar({ show: true, tarea });
  };

  const confirmarEliminarTarea = async () => {
    const tarea = confirmEliminar.tarea;
    setConfirmEliminar({ show: false, tarea: null });
    if (!tarea?.id) return;
    try {
      await tareasService.deleteTarea(tarea.id);
      loadTareas();
    } catch (err) {
      setError(err.response?.data?.error || 'Error al eliminar la tarea');
      console.error(err);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleDateString('es-AR');
  };

  /** Fecha y hora en 24 h (entregas pendientes). */
  const formatDateTime = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleString('es-AR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    });
  };

  /** Solo fecha (ej. fecha_cierre): evita UTC para no mostrar un día menos en zonas negativas. */
  const formatDateOnly = (dateString) => {
    if (!dateString) return '-';
    const s = String(dateString).trim().slice(0, 10);
    const parts = s.split('-').map(Number);
    if (parts.length !== 3 || parts.some(isNaN)) return formatDate(dateString);
    const [y, m, d] = parts;
    return new Date(y, m - 1, d).toLocaleDateString('es-AR');
  };

  const getTipoEntregaLabel = (tipo) => {
    const labels = {
      'archivo': '📎 Archivo',
      'enlace': '🔗 Enlace',
      'texto': '📝 Texto',
      'codigo': '💻 Código'
    };
    return labels[tipo] || tipo;
  };

  /** fecha_cierre descendente (más nueva primero); sin fecha al final; desempate created_at desc */
  const tareasFiltradas = useMemo(() => {
    const raw = selectedCursoId
      ? (tareas || []).filter((t) => t.curso === parseInt(selectedCursoId, 10))
      : [];
    return [...raw].sort((a, b) => {
      const fa = (a.fecha_cierre || '').toString().slice(0, 10);
      const fb = (b.fecha_cierre || '').toString().slice(0, 10);
      if (!fa && !fb) {
        return (b.created_at || '').toString().localeCompare((a.created_at || '').toString());
      }
      if (!fa) return 1;
      if (!fb) return -1;
      if (fb !== fa) return fb.localeCompare(fa);
      return (b.created_at || '').toString().localeCompare((a.created_at || '').toString());
    });
  }, [tareas, selectedCursoId]);

  const totalPagesListado = Math.max(1, Math.ceil(tareasFiltradas.length / itemsPerPage) || 1);
  const pageListadoActual = Math.min(Math.max(1, pageListado), totalPagesListado);
  const startListado = (pageListadoActual - 1) * itemsPerPage;
  const endListado = startListado + itemsPerPage;
  const tareasPaginadas = tareasFiltradas.slice(startListado, endListado);

  // Alinear página si quedó fuera de rango tras filtrar o cambiar total
  useEffect(() => {
    if (pagePendientes !== pagePendientesActual) {
      setPagePendientes(pagePendientesActual);
    }
  }, [pagePendientes, pagePendientesActual]);

  useEffect(() => {
    if (pageCorregidas !== pageCorregidasActual) {
      setPageCorregidas(pageCorregidasActual);
    }
  }, [pageCorregidas, pageCorregidasActual]);

  useEffect(() => {
    if (pageListado !== pageListadoActual) {
      setPageListado(pageListadoActual);
    }
  }, [pageListado, pageListadoActual]);
  useEffect(() => {
    setPageListado(1);
  }, [selectedCursoId]);

  const tareasParaHabilitar = useMemo(() => {
    if (!habCursoId) return [];
    const raw = (tareas || []).filter((t) => t.curso === parseInt(habCursoId, 10));
    return [...raw].sort((a, b) => {
      const fa = (a.fecha_cierre || '').toString().slice(0, 10);
      const fb = (b.fecha_cierre || '').toString().slice(0, 10);
      if (fb !== fa) return fb.localeCompare(fa);
      return (b.created_at || '').toString().localeCompare((a.created_at || '').toString());
    });
  }, [tareas, habCursoId]);

  const fechaLocalMasDias = (dias = 7) => {
    const d = new Date();
    d.setDate(d.getDate() + dias);
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
  };

  const loadPlanilla = useCallback(async (tareaId) => {
    if (!tareaId) {
      setPlanilla(null);
      return;
    }
    setLoadingPlanilla(true);
    setErrorPlanilla('');
    try {
      const data = await tareasService.getPlanillaHabilitacion(tareaId);
      setPlanilla(data);
    } catch (err) {
      setPlanilla(null);
      setErrorPlanilla(err.response?.data?.error || 'No se pudo cargar la planilla.');
    } finally {
      setLoadingPlanilla(false);
    }
  }, []);

  useEffect(() => {
    if (tabActiva !== 'habilitar') return;
    if (!habTareaId) {
      setPlanilla(null);
      return;
    }
    loadPlanilla(habTareaId);
  }, [tabActiva, habTareaId, loadPlanilla]);

  useEffect(() => {
    setHabTareaId('');
    setPlanilla(null);
    setErrorPlanilla('');
    setBusquedaHab('');
    setPageHab(1);
    setHabilitarForm({ show: false, estudiante: null, fechaLimite: '' });
  }, [habCursoId]);

  useEffect(() => {
    setBusquedaHab('');
    setPageHab(1);
  }, [habTareaId]);

  useEffect(() => {
    setPageHab(1);
  }, [busquedaHab]);

  const estudiantesPlanillaFiltrados = useMemo(() => {
    const list = planilla?.estudiantes || [];
    const q = busquedaHab.trim().toLowerCase();
    if (!q) return list;
    return list.filter((est) => {
      const haystack = [est.alumno_nombre, est.alumno_email]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();
      return haystack.includes(q);
    });
  }, [planilla, busquedaHab]);

  const totalHab = estudiantesPlanillaFiltrados.length;
  const totalPagesHab = Math.max(1, Math.ceil(totalHab / itemsPerPage) || 1);
  const pageHabActual = Math.min(Math.max(1, pageHab), totalPagesHab);
  const estudiantesPlanillaPaginados = useMemo(() => {
    const start = (pageHabActual - 1) * itemsPerPage;
    return estudiantesPlanillaFiltrados.slice(start, start + itemsPerPage);
  }, [estudiantesPlanillaFiltrados, pageHabActual, itemsPerPage]);

  useEffect(() => {
    if (pageHab !== pageHabActual) {
      setPageHab(pageHabActual);
    }
  }, [pageHab, pageHabActual]);

  const cambiarPaginaHab = useCallback((page) => {
    setPageHab(Math.max(1, Number(page) || 1));
  }, []);

  const abrirHabilitar = (estudiante) => {
    setHabilitarForm({
      show: true,
      estudiante,
      fechaLimite: estudiante.fecha_limite_habilitacion
        ? String(estudiante.fecha_limite_habilitacion).slice(0, 10)
        : fechaLocalMasDias(7),
    });
  };

  const confirmarHabilitar = async () => {
    if (!habTareaId || !habilitarForm.estudiante || !habilitarForm.fechaLimite) return;
    setSavingHab(true);
    setErrorPlanilla('');
    try {
      await tareasService.habilitarEstudiante(
        habTareaId,
        habilitarForm.estudiante.user_id,
        habilitarForm.fechaLimite,
      );
      setHabilitarForm({ show: false, estudiante: null, fechaLimite: '' });
      await loadPlanilla(habTareaId);
    } catch (err) {
      setErrorPlanilla(err.response?.data?.error || 'No se pudo habilitar la entrega.');
    } finally {
      setSavingHab(false);
    }
  };

  const confirmarDeshabilitar = async () => {
    if (!habTareaId || !confirmDeshab.estudiante) return;
    setSavingHab(true);
    setErrorPlanilla('');
    try {
      await tareasService.deshabilitarEstudiante(habTareaId, confirmDeshab.estudiante.user_id);
      setConfirmDeshab({ show: false, estudiante: null });
      await loadPlanilla(habTareaId);
    } catch (err) {
      setErrorPlanilla(err.response?.data?.error || 'No se pudo quitar la habilitación.');
    } finally {
      setSavingHab(false);
    }
  };

  const estadoRetornoCorreccion = (subTab) => ({
    tab: 'correccion',
    subTab,
    page: subTab === 'pendientes' ? pagePendientes : pageCorregidas,
    cursoFiltro: cursoFiltroCorreccion,
  });

  const irACorregir = async (entregaId) => {
    try {
      await tareasService.getEntregaParaCorregir(entregaId, { modo: 'corregir' });
      navigate(`/dashboard/corregir/${entregaId}`, {
        state: { returnTo: estadoRetornoCorreccion('pendientes') },
      });
    } catch (err) {
      const data = err.response?.data || {};
      if (err.response?.status === 409 || data.code === 'ya_corregida') {
        setAvisoYaCorregida(data.error || 'Esta entrega ya fue corregida.');
        loadPendientes(pagePendientesRef.current);
        return;
      }
      setError(data.error || 'No se pudo abrir la entrega para corregir.');
    }
  };

  const irAVerCorreccion = (entregaId) => {
    navigate(`/dashboard/ver-correccion/${entregaId}`, {
      state: { returnTo: estadoRetornoCorreccion('corregidas') },
    });
  };

  const abrirDetalleGrupo = (entrega) => {
    const nombre = entrega?.grupo_nombre || entrega?.alumno_nombre || 'Grupo';
    const alumnos = Array.isArray(entrega?.alumnos) ? entrega.alumnos : [];
    setGrupoDetalle({ show: true, nombre, alumnos });
  };

  const renderCeldaAlumno = (entrega) => {
    const tieneGrupo = Boolean(
      entrega?.entrega_en_grupos
      && (
        entrega?.grupo_nombre
        || (Array.isArray(entrega?.alumnos) && entrega.alumnos.length > 0)
      )
    );
    const label = (tieneGrupo ? (entrega?.grupo_nombre || entrega?.alumno_nombre) : entrega?.alumno_nombre)
      || entrega?.alumno_id
      || '-';
    if (!tieneGrupo) {
      return formatPersonName(label) || label;
    }
    return (
      <button
        type="button"
        className="tareas-grupo-nombre-btn"
        onClick={() => abrirDetalleGrupo(entrega)}
        title="Ver integrantes del grupo"
      >
        {label}
      </button>
    );
  };

  const onCambioFiltroCursoCorreccion = (valor) => {
    setCursoFiltroCorreccion(valor);
    setPagePendientes(1);
    setPageCorregidas(1);
  };

  if (loading && tareas.length === 0) {
    return (
      <div className="tareas-loading">
        <div className="loading-spinner"></div>
        <p>Cargando tareas...</p>
      </div>
    );
  }

  return (
    <div className="tareas-container">
      <div className="tareas-header">
        <h1>Gestión de Tareas</h1>
        {!isAyudante && (
          <div className="tareas-actions">
            <button
              className="tareas-btn tareas-btn-primary"
              onClick={() => navigate('/dashboard/tareas/nuevo')}
            >
              ➕ Nueva Tarea
            </button>
          </div>
        )}
      </div>

      <ConfirmPopup
        open={Boolean(error)}
        title="Error"
        message={error}
        confirmLabel="Aceptar"
        cancelLabel={null}
        onConfirm={() => setError('')}
        onCancel={() => setError('')}
        variant="danger"
      />

      <ConfirmPopup
        open={Boolean(avisoYaCorregida)}
        title="Entrega ya corregida"
        message={avisoYaCorregida}
        confirmLabel="Aceptar"
        cancelLabel={null}
        onConfirm={() => setAvisoYaCorregida('')}
        onCancel={() => setAvisoYaCorregida('')}
        variant="primary"
      />

      <ConfirmPopup
        open={confirmEliminar.show}
        title="Eliminar tarea"
        message={
          confirmEliminar.tarea?.titulo
            ? `¿Estás seguro de que deseás eliminar la tarea «${confirmEliminar.tarea.titulo}»?`
            : '¿Estás seguro de que deseás eliminar esta tarea?'
        }
        confirmLabel="Eliminar"
        cancelLabel="Cancelar"
        variant="danger"
        onConfirm={confirmarEliminarTarea}
        onCancel={() => setConfirmEliminar({ show: false, tarea: null })}
      />

      <ConfirmPopup
        open={confirmDeshab.show}
        title="Quitar habilitación"
        message={
          confirmDeshab.estudiante?.alumno_nombre
            ? `¿Quitar la habilitación de entrega para «${confirmDeshab.estudiante.alumno_nombre}»?`
            : '¿Quitar la habilitación de entrega?'
        }
        confirmLabel="Quitar"
        cancelLabel="Cancelar"
        variant="danger"
        onConfirm={confirmarDeshabilitar}
        onCancel={() => setConfirmDeshab({ show: false, estudiante: null })}
      />

      {previewTarea && (
        <div
          className="tareas-modal-overlay"
          onClick={(e) => {
            if (e.target === e.currentTarget) setPreviewTarea(null);
          }}
          role="dialog"
          aria-modal="true"
          aria-labelledby="tareas-preview-title"
        >
          <div className="tareas-modal tareas-preview-modal">
            <div className="tareas-modal-header">
              <h2 id="tareas-preview-title">Vista previa</h2>
              <button
                type="button"
                className="tareas-modal-close"
                onClick={() => setPreviewTarea(null)}
                aria-label="Cerrar"
              >
                ×
              </button>
            </div>
            <div className="tareas-preview-modal-body">
              <h3 className="tareas-preview-titulo">{previewTarea.titulo || 'Tarea'}</h3>
              {previewTarea.curso_nombre && (
                <p className="tareas-preview-fecha"><strong>Curso:</strong> {previewTarea.curso_nombre}</p>
              )}
              {previewTarea.descripcion ? (
                <div className="tareas-preview-desc">
                  <HtmlContent content={previewTarea.descripcion} tag="div" />
                </div>
              ) : (
                <p className="tareas-preview-sin-desc">Sin descripción</p>
              )}
              {Array.isArray(previewTarea.archivos_material) && previewTarea.archivos_material.length > 0 && (
                <div className="tareas-preview-material">
                  <strong>Material adjunto:</strong>
                  <ul className="tareas-preview-material-list">
                    {previewTarea.archivos_material.map((a) => (
                      <li key={a.id}>
                        {a.url ? (
                          <a href={a.url} target="_blank" rel="noopener noreferrer">📎 {a.nombre_original || 'Archivo'}</a>
                        ) : (
                          <span>📎 {a.nombre_original || 'Archivo'}</span>
                        )}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              {previewTarea.fecha_cierre && (
                <p className="tareas-preview-fecha">
                  <strong>Fecha de cierre:</strong> {formatDateOnly(previewTarea.fecha_cierre)}
                </p>
              )}
              <div className="tareas-preview-opciones">
                {previewTarea.se_califica && <span className="tareas-preview-badge">Se califica</span>}
                {previewTarea.es_obligatorio && <span className="tareas-preview-badge">Obligatorio</span>}
                {previewTarea.aceptar_vencidos && <span className="tareas-preview-badge">Acepta vencidos</span>}
                {previewTarea.entrega_en_grupos && (
                  <span className="tareas-preview-badge">
                    En grupos
                    {previewTarea.max_integrantes_por_grupo
                      ? ` (máx. ${previewTarea.max_integrantes_por_grupo})`
                      : ''}
                  </span>
                )}
              </div>
              {Array.isArray(previewTarea.tipos_entrega) && previewTarea.tipos_entrega.length > 0 && (
                <div className="tareas-preview-tipos">
                  <strong>Tipos de entrega:</strong>
                  <div className="tareas-preview-tipos-list">
                    {previewTarea.tipos_entrega.map((tipo) => (
                      <span key={tipo} className="tareas-preview-tipo-badge">{getTipoEntregaLabel(tipo)}</span>
                    ))}
                  </div>
                  {previewTarea.tipos_entrega.includes('archivo') && (previewTarea.tipos_archivo_entrega || []).length > 0 && (
                    <p className="tareas-preview-archivo-tipos">
                      <strong>Archivos:</strong>{' '}
                      {TIPOS_ARCHIVO_ENTREGA.filter((t) => previewTarea.tipos_archivo_entrega.includes(t.id))
                        .map((t) => {
                          if (t.id === 'script') {
                            const custom = (previewTarea.extensiones_script || '')
                              .split(',')
                              .map((e) => e.trim().replace(/^\./, ''))
                              .filter(Boolean)
                              .map((e) => `.${e}`)
                              .join(', ');
                            return `Script (${custom || '.py, .js, .html, .css'})`;
                          }
                          return t.label;
                        })
                        .join(', ')}
                    </p>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {grupoDetalle.show && (
        <div
          className="confirm-popup-overlay"
          onClick={(e) => {
            if (e.target === e.currentTarget) {
              setGrupoDetalle({ show: false, nombre: '', alumnos: [] });
            }
          }}
          role="dialog"
          aria-modal="true"
          aria-labelledby="tareas-grupo-detalle-title"
        >
          <div className="confirm-popup-box tareas-grupo-detalle-modal" onClick={(e) => e.stopPropagation()}>
            <h3 id="tareas-grupo-detalle-title" className="confirm-popup-title">
              {grupoDetalle.nombre || 'Grupo'}
            </h3>
            {grupoDetalle.alumnos.length === 0 ? (
              <p className="confirm-popup-message">No hay integrantes para mostrar.</p>
            ) : (
              <ul className="tareas-grupo-integrantes-lista">
                {grupoDetalle.alumnos.map((a) => (
                  <li key={a.id || a.nombre}>
                    {formatPersonName(a.nombre) || a.nombre || '-'}
                  </li>
                ))}
              </ul>
            )}
            <div className="confirm-popup-actions">
              <button
                type="button"
                className="confirm-popup-btn confirm-popup-btn-confirm confirm-popup-btn-primary"
                onClick={() => setGrupoDetalle({ show: false, nombre: '', alumnos: [] })}
              >
                Cerrar
              </button>
            </div>
          </div>
        </div>
      )}

      {!isAyudante && (
        <div className="tareas-tabs">
          <button
            type="button"
            className={`tareas-tab ${tabActiva === 'listado' ? 'tareas-tab-activa' : ''}`}
            onClick={() => setTabActiva('listado')}
          >
            Listado de tareas
          </button>
          <button
            type="button"
            className={`tareas-tab ${tabActiva === 'correccion' ? 'tareas-tab-activa' : ''}`}
            onClick={() => setTabActiva('correccion')}
          >
            Corrección de tareas
          </button>
          <button
            type="button"
            className={`tareas-tab ${tabActiva === 'habilitar' ? 'tareas-tab-activa' : ''}`}
            onClick={() => setTabActiva('habilitar')}
          >
            Habilitar tarea
          </button>
        </div>
      )}

      {(isAyudante || tabActiva === 'correccion') ? (
        <div className="tareas-correccion-section">
          <div className="tareas-correccion-tabs">
            <button
              type="button"
              className={`tareas-tab ${tabCorreccion === 'pendientes' ? 'tareas-tab-activa' : ''}`}
              onClick={() => setTabCorreccion('pendientes')}
            >
              Pendientes
            </button>
            <button
              type="button"
              className={`tareas-tab ${tabCorreccion === 'corregidas' ? 'tareas-tab-activa' : ''}`}
              onClick={() => setTabCorreccion('corregidas')}
            >
              Corregidas
            </button>
          </div>
          {tabCorreccion === 'pendientes' ? (
            <>
              <h2 className="tareas-correccion-title">Tareas pendientes de corrección</h2>
              <div className="tareas-correccion-filtro">
                {cursosUnicosCorreccion.length > 0 && (
                  <>
                    <label htmlFor="tareas-filtro-curso-pendientes" className="tareas-correccion-filtro-label">Filtrar por curso</label>
                    <select
                      id="tareas-filtro-curso-pendientes"
                      className="tareas-correccion-filtro-select"
                      value={cursoFiltroCorreccion}
                      onChange={(e) => onCambioFiltroCursoCorreccion(e.target.value)}
                    >
                      <option value="">Todos los cursos</option>
                      {cursosUnicosCorreccion.map((c) => (
                        <option key={c.id} value={c.id}>{c.nombre}</option>
                      ))}
                    </select>
                  </>
                )}
                <label htmlFor="tareas-busqueda-pendientes" className="tareas-correccion-filtro-label">Buscar estudiante</label>
                <input
                  id="tareas-busqueda-pendientes"
                  type="search"
                  className="tareas-busqueda-input"
                  placeholder="Nombre, apellido o correo..."
                  value={busquedaCorreccion}
                  onChange={(e) => setBusquedaCorreccion(e.target.value)}
                />
              </div>
              {loadingPendientes && pendientes.length === 0 ? (
                <div className="tareas-loading">
                  <div className="loading-spinner"></div>
                  <p>Cargando entregas...</p>
                </div>
              ) : (
                <div className={`tareas-table-container ${loadingPendientes ? 'pagination-content-loading' : ''}`}>
                  <table className="tareas-table">
                    <thead>
                      <tr>
                        <th>Alumno</th>
                        <th>Curso</th>
                        <th>Tarea</th>
                        <th>Fecha entrega</th>
                        <th>Seguimiento</th>
                        <th>Acción</th>
                      </tr>
                    </thead>
                    <tbody>
                      {totalPendientes === 0 ? (
                        <tr>
                          <td colSpan="6" className="tareas-empty">
                            {busquedaCorreccion.trim()
                              ? 'No hay entregas que coincidan con la búsqueda'
                              : cursoFiltroCorreccion
                                ? 'No hay entregas en el curso seleccionado'
                                : 'No hay entregas pendientes de corrección'}
                          </td>
                        </tr>
                      ) : (
                        pendientes.map((e) => (
                          <tr key={e.id}>
                            <td>{renderCeldaAlumno(e)}</td>
                            <td>{e.curso_nombre || '-'}</td>
                            <td>{e.tarea_titulo || '-'}</td>
                            <td className={e.fuera_de_plazo ? 'tareas-fecha-fuera-de-plazo' : ''}>{formatDateTime(e.fecha_entrega)}</td>
                            <td>{e.seguimiento || '-'}</td>
                            <td>
                              <button
                                type="button"
                                className="tareas-btn tareas-btn-primary"
                                onClick={() => irACorregir(e.id)}
                              >
                                Corregir
                              </button>
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              )}
              {totalPendientes > itemsPerPage && (
                <Pagination
                  currentPage={pagePendientesActual}
                  totalPages={totalPagesPendientes}
                  onPageChange={cambiarPaginaPendientes}
                />
              )}
            </>
          ) : (
            <>
              <h2 className="tareas-correccion-title">Tareas corregidas</h2>
              <div className="tareas-correccion-filtro">
                {cursosUnicosCorreccion.length > 0 && (
                  <>
                    <label htmlFor="tareas-filtro-curso-corregidas" className="tareas-correccion-filtro-label">Filtrar por curso</label>
                    <select
                      id="tareas-filtro-curso-corregidas"
                      className="tareas-correccion-filtro-select"
                      value={cursoFiltroCorreccion}
                      onChange={(e) => onCambioFiltroCursoCorreccion(e.target.value)}
                    >
                      <option value="">Todos los cursos</option>
                      {cursosUnicosCorreccion.map((c) => (
                        <option key={c.id} value={c.id}>{c.nombre}</option>
                      ))}
                    </select>
                  </>
                )}
                <label htmlFor="tareas-busqueda-corregidas" className="tareas-correccion-filtro-label">Buscar estudiante</label>
                <input
                  id="tareas-busqueda-corregidas"
                  type="search"
                  className="tareas-busqueda-input"
                  placeholder="Nombre, apellido o correo..."
                  value={busquedaCorreccion}
                  onChange={(e) => setBusquedaCorreccion(e.target.value)}
                />
              </div>
              {loadingCorregidas && corregidas.length === 0 ? (
                <div className="tareas-loading">
                  <div className="loading-spinner"></div>
                  <p>Cargando entregas...</p>
                </div>
              ) : (
                <div className={`tareas-table-container ${loadingCorregidas ? 'pagination-content-loading' : ''}`}>
                  <table className="tareas-table">
                    <thead>
                      <tr>
                        <th>Alumno</th>
                        <th>Curso</th>
                        <th>Tarea</th>
                        <th>Fecha corrección</th>
                        <th>Quién corrigió</th>
                        <th>Seguimiento</th>
                        <th>Estado</th>
                        <th>Acción</th>
                      </tr>
                    </thead>
                    <tbody>
                      {totalCorregidas === 0 ? (
                        <tr>
                          <td colSpan="8" className="tareas-empty">
                            {busquedaCorreccion.trim()
                              ? 'No hay entregas que coincidan con la búsqueda'
                              : cursoFiltroCorreccion
                                ? 'No hay entregas en el curso seleccionado'
                                : 'No hay entregas corregidas'}
                          </td>
                        </tr>
                      ) : (
                        corregidas.map((e) => (
                          <tr key={e.id}>
                            <td>{renderCeldaAlumno(e)}</td>
                            <td>{e.curso_nombre || '-'}</td>
                            <td>{e.tarea_titulo || '-'}</td>
                            <td>{formatDate(e.fecha_revision)}</td>
                            <td>{e.corregido_por_nombre || '-'}</td>
                            <td>{e.seguimiento || '-'}</td>
                            <td>
                              <span className={`tareas-estado-badge tareas-estado-${e.estado?.toLowerCase() || ''}`}>
                                {e.estado || '-'}
                              </span>
                            </td>
                            <td>
                              <button
                                type="button"
                                className="tareas-btn tareas-btn-secondary"
                                onClick={() => irAVerCorreccion(e.id)}
                              >
                                Ver tarea y devolución
                              </button>
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              )}
              {totalCorregidas > itemsPerPage && (
                <Pagination
                  currentPage={pageCorregidasActual}
                  totalPages={totalPagesCorregidas}
                  onPageChange={cambiarPaginaCorregidas}
                />
              )}
            </>
          )}
        </div>
      ) : tabActiva === 'habilitar' ? (
        <div className="tareas-habilitar-section">
          <h2 className="tareas-correccion-title">Habilitar entrega por estudiante</h2>
          <p className="tareas-habilitar-hint">
            Seleccioná un curso y una tarea para ver quién entregó. Podés habilitar la entrega solo a quienes aún no entregaron.
          </p>
          <div className="tareas-curso-selector tareas-habilitar-selectores">
            <div className="tareas-form-group">
              <label htmlFor="selector-curso-hab">Curso</label>
              <select
                id="selector-curso-hab"
                className="tareas-select-curso"
                value={habCursoId}
                onChange={(e) => setHabCursoId(e.target.value || '')}
              >
                <option value="">Seleccionar curso</option>
                {cursosParaSelector.map((curso) => (
                  <option key={curso.id} value={curso.id}>
                    {curso.nombre}
                    {curso.activo === false ? ' (inactivo)' : ''}
                  </option>
                ))}
              </select>
            </div>
            <div className="tareas-form-group">
              <label htmlFor="selector-tarea-hab">Tarea</label>
              <select
                id="selector-tarea-hab"
                className="tareas-select-curso"
                value={habTareaId}
                disabled={!habCursoId}
                onChange={(e) => setHabTareaId(e.target.value || '')}
              >
                <option value="">{habCursoId ? 'Seleccionar tarea' : 'Primero elegí un curso'}</option>
                {tareasParaHabilitar.map((t) => (
                  <option key={t.id} value={t.id}>
                    {t.titulo || `Tarea ${t.id}`}
                    {t.fecha_cierre ? ` (cierre ${formatDateOnly(t.fecha_cierre)})` : ''}
                  </option>
                ))}
              </select>
            </div>
            <label className="tareas-checkbox-label tareas-checkbox-inactivos">
              <input
                type="checkbox"
                checked={mostrarCursosInactivos}
                onChange={(e) => setMostrarCursosInactivos(e.target.checked)}
              />
              <span>Mostrar solo cursos inactivos</span>
            </label>
          </div>

          {errorPlanilla && (
            <div className="tareas-error" role="alert">{errorPlanilla}</div>
          )}

          {habilitarForm.show && habilitarForm.estudiante && (
            <div
              className="confirm-popup-overlay"
              onClick={(e) => {
                if (e.target === e.currentTarget && !savingHab) {
                  setHabilitarForm({ show: false, estudiante: null, fechaLimite: '' });
                }
              }}
              role="dialog"
              aria-modal="true"
              aria-labelledby="tareas-habilitar-modal-title"
            >
              <div className="confirm-popup-box tareas-habilitar-modal" onClick={(e) => e.stopPropagation()}>
                <h3 id="tareas-habilitar-modal-title" className="confirm-popup-title">
                  {habilitarForm.estudiante.habilitada ? 'Modificar habilitación' : 'Habilitar entrega'}
                </h3>
                <p className="confirm-popup-message">
                  Estudiante: <strong>{habilitarForm.estudiante.alumno_nombre}</strong>
                </p>
                <div className="tareas-form-group tareas-habilitar-modal-field">
                  <label htmlFor="fecha-limite-hab">Fecha límite</label>
                  <input
                    id="fecha-limite-hab"
                    type="date"
                    className="tareas-select-curso"
                    value={habilitarForm.fechaLimite}
                    disabled={savingHab}
                    onChange={(e) => setHabilitarForm((prev) => ({ ...prev, fechaLimite: e.target.value }))}
                  />
                </div>
                <div className="confirm-popup-actions">
                  <button
                    type="button"
                    className="confirm-popup-btn confirm-popup-btn-cancel"
                    disabled={savingHab}
                    onClick={() => setHabilitarForm({ show: false, estudiante: null, fechaLimite: '' })}
                  >
                    Cancelar
                  </button>
                  <button
                    type="button"
                    className="confirm-popup-btn confirm-popup-btn-confirm confirm-popup-btn-primary"
                    disabled={savingHab || !habilitarForm.fechaLimite}
                    onClick={confirmarHabilitar}
                  >
                    {savingHab ? 'Guardando…' : 'Confirmar'}
                  </button>
                </div>
              </div>
            </div>
          )}

          {!habTareaId ? (
            <div className="tareas-sin-curso">
              <p>Seleccioná un curso y una tarea para ver la planilla de estudiantes.</p>
            </div>
          ) : loadingPlanilla && !planilla ? (
            <div className="tareas-loading">
              <div className="loading-spinner"></div>
              <p>Cargando planilla...</p>
            </div>
          ) : (
            <>
            <div className="tareas-correccion-filtro">
              <label htmlFor="tareas-busqueda-habilitar" className="tareas-correccion-filtro-label">Buscar estudiante</label>
              <input
                id="tareas-busqueda-habilitar"
                type="search"
                className="tareas-busqueda-input"
                placeholder="Nombre, apellido o correo..."
                value={busquedaHab}
                onChange={(e) => setBusquedaHab(e.target.value)}
              />
            </div>
            <div className={`tareas-table-container ${loadingPlanilla ? 'pagination-content-loading' : ''}`}>
              <table className="tareas-table">
                <thead>
                  <tr>
                    <th>Estudiante</th>
                    <th>Email</th>
                    <th>Estado</th>
                    <th>Habilitación</th>
                    <th>Acción</th>
                  </tr>
                </thead>
                <tbody>
                  {!planilla?.estudiantes?.length ? (
                    <tr>
                      <td colSpan="5" className="tareas-empty">
                        No hay estudiantes activos en este curso
                      </td>
                    </tr>
                  ) : estudiantesPlanillaFiltrados.length === 0 ? (
                    <tr>
                      <td colSpan="5" className="tareas-empty">
                        No hay estudiantes que coincidan con la búsqueda
                      </td>
                    </tr>
                  ) : (
                    estudiantesPlanillaPaginados.map((est) => (
                      <tr key={est.user_id}>
                        <td>{est.alumno_nombre || '-'}</td>
                        <td>{est.alumno_email || '-'}</td>
                        <td>
                          <span className={`tareas-status ${est.entregada ? 'yes' : 'no'}`}>
                            {est.estado}
                          </span>
                          {est.fecha_entrega && (
                            <span className="tareas-habilitar-fecha-entrega">
                              {' '}({formatDateTime(est.fecha_entrega)})
                            </span>
                          )}
                        </td>
                        <td>
                          {est.habilitada
                            ? `Hasta ${formatDateOnly(est.fecha_limite_habilitacion)}`
                            : '—'}
                        </td>
                        <td>
                          {est.puede_habilitar ? (
                            <div className="tareas-actions-cell">
                              <button
                                type="button"
                                className="tareas-btn-action tareas-btn-habilitar"
                                title={est.habilitada ? 'Modificar habilitación' : 'Habilitar entrega'}
                                disabled={savingHab}
                                onClick={() => abrirHabilitar(est)}
                              >
                                {est.habilitada ? '✏️' : '🔓'}
                              </button>
                              {est.habilitada && (
                                <button
                                  type="button"
                                  className="tareas-btn-action tareas-btn-delete"
                                  title="Quitar habilitación"
                                  disabled={savingHab}
                                  onClick={() => setConfirmDeshab({ show: true, estudiante: est })}
                                >
                                  🗑️
                                </button>
                              )}
                            </div>
                          ) : (
                            <span className="tareas-habilitar-sin-accion">—</span>
                          )}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
            {totalHab > itemsPerPage && (
              <Pagination
                currentPage={pageHabActual}
                totalPages={totalPagesHab}
                onPageChange={cambiarPaginaHab}
              />
            )}
            </>
          )}
        </div>
      ) : (
      <>
      <div className="tareas-curso-selector">
        <div className="tareas-form-group">
          <label htmlFor="selector-curso">Curso</label>
          <select
            id="selector-curso"
            className="tareas-select-curso"
            value={selectedCursoId}
            onChange={(e) => setSelectedCursoId(e.target.value || '')}
          >
            <option value="">Seleccionar curso</option>
            {cursosParaSelector.map((curso) => (
              <option key={curso.id} value={curso.id}>
                {curso.nombre}
                {curso.activo === false ? ' (inactivo)' : ''}
              </option>
            ))}
          </select>
        </div>
        <label className="tareas-checkbox-label tareas-checkbox-inactivos">
          <input
            type="checkbox"
            checked={mostrarCursosInactivos}
            onChange={(e) => setMostrarCursosInactivos(e.target.checked)}
          />
          <span>Mostrar solo cursos inactivos</span>
        </label>
      </div>

      {!selectedCursoId ? (
        <div className="tareas-sin-curso">
          <p>Seleccione un curso en el desplegable para ver las tareas.</p>
        </div>
      ) : (
      <div className="tareas-table-container">
        <table className="tareas-table">
          <thead>
            <tr>
              <th>Título</th>
              <th>Descripción</th>
              <th>Fecha de Cierre</th>
              <th>Se Califica</th>
              <th>Obligatorio</th>
              <th>Tipos de Entrega</th>
              <th>Curso</th>
              <th>Acciones</th>
            </tr>
          </thead>
          <tbody>
            {tareasFiltradas.length === 0 ? (
              <tr>
                <td colSpan="8" className="tareas-empty">
                  No hay tareas para este curso
                </td>
              </tr>
            ) : (
              tareasPaginadas.map((tarea) => (
                <tr key={tarea.id}>
                  <td>{tarea.titulo || '-'}</td>
                  <td className="tareas-descripcion">
                    {tarea.descripcion ? (
                      <HtmlContent content={tarea.descripcion} maxLength={50} className="tareas-descripcion-text" />
                    ) : (
                      '-'
                    )}
                  </td>
                  <td>{formatDateOnly(tarea.fecha_cierre)}</td>
                  <td>
                    <span className={`tareas-status ${tarea.se_califica ? 'yes' : 'no'}`}>
                      {tarea.se_califica ? 'Sí' : 'No'}
                    </span>
                  </td>
                  <td>
                    <span className={`tareas-status ${tarea.es_obligatorio ? 'yes' : 'no'}`}>
                      {tarea.es_obligatorio ? 'Sí' : 'No'}
                    </span>
                  </td>
                  <td>
                    {tarea.tipos_entrega && tarea.tipos_entrega.length > 0 ? (
                      <div className="tareas-tipos">
                        {tarea.tipos_entrega.map((tipo, index) => (
                          <span key={index} className="tareas-tipo-badge">
                            {getTipoEntregaLabel(tipo)}
                          </span>
                        ))}
                      </div>
                    ) : (
                      '-'
                    )}
                  </td>
                  <td>{tarea.curso_nombre || '-'}</td>
                  <td>
                    <div className="tareas-actions-cell">
                      <button
                        type="button"
                        className="tareas-btn-action tareas-btn-preview"
                        onClick={() => setPreviewTarea(tarea)}
                        title="Previsualizar"
                        aria-label={`Previsualizar ${tarea.titulo || 'tarea'}`}
                      >
                        👁️
                      </button>
                      <button
                        type="button"
                        className="tareas-btn-action tareas-btn-edit"
                        onClick={() => navigate(`/dashboard/tareas/${tarea.id}/editar`)}
                        title="Editar"
                      >
                        ✏️
                      </button>
                      <button
                        className="tareas-btn-action tareas-btn-delete"
                        onClick={() => handleDelete(tarea)}
                        title="Eliminar"
                      >
                        🗑️
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
      )}
      {totalPagesListado > 1 && (
        <Pagination
          currentPage={pageListadoActual}
          totalPages={totalPagesListado}
          onPageChange={cambiarPaginaListado}
        />
      )}
      </>
      )}

    </div>
  );
};

export default Tareas;
